/**
 * SLA display math (SOUND-534, Ticketing Phase 5.1).
 *
 * Pure, presentation-only helpers over the `ticket.sla{}` block the backend
 * stamps (slaService.mjs). The BACKEND is the source of truth for the due dates
 * — we only derive the *color* (approaching / breached) and a relative countdown
 * from those dates, using the SAME anchor + pausedMs logic as the engine so the
 * UI never disagrees with an alert.
 *
 * The `sla{}` block (from slaService.emptySla / computeSla):
 *   policyId, firstResponseTargetMins, resolutionTargetMins,
 *   firstResponseDueAt, resolutionDueAt, firstRespondedAt,
 *   paused, pauseStartedAt, pausedMs, alertedThresholds[]
 *
 * Threshold parity with the engine (services/slaService.mjs):
 *   due       = anchor(createdAt) + target*60000 + pausedMs
 *   approachAt= anchor(createdAt) + 0.8*target*60000 + pausedMs
 *             = due - (1 - 0.8)*target*60000      ← identical, needs only dueAt
 *   breached  ⇔ now >= due ; approaching ⇔ now >= approachAt
 * While paused the clock is FROZEN, so we evaluate at `pauseStartedAt` (the
 * engine excludes paused tickets from the scan and pushes dueAt out on resume).
 */

import { TICKET_STATUS_CATEGORY } from "constants/tickets";

const MIN_MS = 60_000;
export const SLA_APPROACHING_RATIO = 0.8; // mirrors slaService.APPROACHING_RATIO

// Clock states, worst → best.
export const SLA_STATE = {
  BREACHED: "breached",
  APPROACHING: "approaching",
  OK: "ok",
  MET: "met", // clock satisfied (responded / resolved)
};

// Inbox bucket vocabulary (column badge + sort + filter).
export const SLA_BUCKET = {
  BREACHED: "breached",
  APPROACHING: "approaching",
  ON_TRACK: "on_track",
  NONE: "none",
};

export const SLA_BUCKET_OPTIONS = [
  { value: SLA_BUCKET.BREACHED, label: "Breached" },
  { value: SLA_BUCKET.APPROACHING, label: "Approaching" },
  { value: SLA_BUCKET.ON_TRACK, label: "On track" },
  { value: SLA_BUCKET.NONE, label: "No SLA" },
];

export const SLA_CLOCK_LABELS = {
  first_response: "First response",
  resolution: "Resolution",
};

// Design-system tones. Semantic warning/destructive (NEVER the brand accent).
export const SLA_TONE = {
  breached: {
    text: "text-destructive",
    chip: "text-destructive border-destructive/20",
    chipBg: "bg-destructive/10",
    dot: "bg-destructive",
    label: "Breached",
  },
  approaching: {
    text: "text-warning",
    chip: "text-warning border-warning/20",
    chipBg: "bg-warning/[0.14]",
    dot: "bg-warning",
    label: "Due soon",
  },
  ok: {
    text: "text-muted-foreground",
    chip: "text-muted-foreground border-border",
    chipBg: "bg-muted",
    dot: "bg-muted-foreground",
    label: "On track",
  },
  met: {
    text: "text-success",
    chip: "text-success border-success/20",
    chipBg: "bg-success/10",
    dot: "bg-success",
    label: "Met",
  },
  paused: {
    text: "text-muted-foreground",
    chip: "text-muted-foreground border-border",
    chipBg: "bg-muted",
    dot: "bg-muted-foreground",
    label: "Paused",
  },
};

function ms(value) {
  if (value == null) return null;
  const t = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

function categoryOf(ticket) {
  return ticket?.statusCategory || TICKET_STATUS_CATEGORY[ticket?.status] || "todo";
}

/** A ticket is "on the clock" only while its category is todo / in_progress. */
export function isTicketOpen(ticket) {
  const c = categoryOf(ticket);
  return c === "todo" || c === "in_progress";
}

export function hasSla(ticket) {
  return !!(ticket && ticket.sla && ticket.sla.policyId != null);
}

/**
 * Evaluate one SLA clock for display.
 *
 * @returns null when the clock isn't configured/active, else
 *   { clock, active, done, state, dueAt, remainingMs, overdue, paused }
 * `remainingMs` > 0 = time left; < 0 = overdue by |remainingMs| (frozen while paused).
 */
export function computeClock({
  clock,
  targetMins,
  dueAt,
  done = false,
  active = true,
  paused = false,
  pauseStartedAt = null,
  now = Date.now(),
}) {
  const due = ms(dueAt);
  if (targetMins == null || due == null) return null;

  if (done) {
    return { clock, active: false, done: true, state: SLA_STATE.MET, dueAt, remainingMs: null, overdue: false, paused: false };
  }
  if (!active) return null;

  // Frozen evaluation instant while paused (engine pushes dueAt out on resume).
  const frozenNow = paused ? ms(pauseStartedAt) ?? now : now;
  const approachAt = due - (1 - SLA_APPROACHING_RATIO) * targetMins * MIN_MS;

  let state;
  if (frozenNow >= due) state = SLA_STATE.BREACHED;
  else if (frozenNow >= approachAt) state = SLA_STATE.APPROACHING;
  else state = SLA_STATE.OK;

  const remainingMs = due - frozenNow;
  return {
    clock,
    active: true,
    done: false,
    state,
    dueAt,
    remainingMs,
    overdue: remainingMs <= 0,
    paused,
  };
}

/**
 * Whole-ticket SLA summary for the indicators / column / sort / filter / panel.
 */
export function computeTicketSla(ticket, now = Date.now()) {
  if (!hasSla(ticket)) {
    return { hasSla: false, paused: false, isOpen: isTicketOpen(ticket), firstResponse: null, resolution: null, worst: null, bucket: SLA_BUCKET.NONE };
  }
  const sla = ticket.sla;
  const open = isTicketOpen(ticket);
  const paused = sla.paused === true;

  const firstResponse = computeClock({
    clock: "first_response",
    targetMins: sla.firstResponseTargetMins,
    dueAt: sla.firstResponseDueAt,
    // FR clock stops the moment there's a first response; else it runs while open.
    done: !!sla.firstRespondedAt,
    active: open && !sla.firstRespondedAt,
    paused,
    pauseStartedAt: sla.pauseStartedAt,
    now,
  });

  const resolution = computeClock({
    clock: "resolution",
    targetMins: sla.resolutionTargetMins,
    dueAt: sla.resolutionDueAt,
    done: !open,
    active: open,
    paused,
    pauseStartedAt: sla.pauseStartedAt,
    now,
  });

  // Worst ACTIVE clock drives the badge/sort (breached > approaching > ok).
  const active = [firstResponse, resolution].filter((c) => c && c.active);
  const rank = { breached: 0, approaching: 1, ok: 2 };
  let worst = null;
  for (const c of active) {
    if (!worst || rank[c.state] < rank[worst.state]) worst = c;
  }

  let bucket = SLA_BUCKET.ON_TRACK;
  if (worst?.state === SLA_STATE.BREACHED) bucket = SLA_BUCKET.BREACHED;
  else if (worst?.state === SLA_STATE.APPROACHING) bucket = SLA_BUCKET.APPROACHING;

  return { hasSla: true, paused, isOpen: open, firstResponse, resolution, worst, bucket };
}

/** Inbox filter bucket for a ticket (breached / approaching / on_track / none). */
export function slaBucketOf(ticket, now = Date.now()) {
  return computeTicketSla(ticket, now).bucket;
}

const BUCKET_RANK = { breached: 0, approaching: 1, on_track: 2, none: 3 };

/**
 * Comparator: breached-first, then soonest active due date. Ascending due date
 * naturally puts the most-overdue breach first and the soonest-due next.
 */
export function compareTicketsBySla(a, b, now = Date.now()) {
  const sa = computeTicketSla(a, now);
  const sb = computeTicketSla(b, now);
  const ra = BUCKET_RANK[sa.bucket];
  const rb = BUCKET_RANK[sb.bucket];
  if (ra !== rb) return ra - rb;
  const da = ms(sa.worst?.dueAt) ?? Number.MAX_SAFE_INTEGER;
  const db = ms(sb.worst?.dueAt) ?? Number.MAX_SAFE_INTEGER;
  return da - db;
}

/** "2h left" / "overdue 40m" / "due now" — from a signed remaining-ms. */
/**
 * Magnitude only — "21h", not "overdue 21h".
 *
 * For the inbox SLA column, where a triangle already means breached and a clock
 * means running: the words are the icon said twice, and they cost enough width
 * to truncate the value itself ("overdue 7d 12h" clipping mid-number). The full
 * phrasing stays in formatCountdown for SlaPanel on the ticket record, which has
 * room and no icon carrying the distinction.
 *
 * Callers MUST convey breached-vs-running some other way (icon + tone) and put
 * the full text in a title — this string alone is ambiguous by design.
 */
export function formatCountdownMagnitude(remainingMs) {
  if (remainingMs == null || Number.isNaN(remainingMs)) return "";
  const abs = Math.abs(remainingMs);
  const totalMin = Math.round(abs / MIN_MS);
  if (totalMin < 1) return remainingMs <= 0 ? "now" : "<1m";
  if (totalMin < 60) return `${totalMin}m`;
  if (totalMin < 60 * 24) return `${Math.round(totalMin / 60)}h`;
  return `${Math.round(totalMin / (60 * 24))}d`;
}

export function formatCountdown(remainingMs) {
  if (remainingMs == null || Number.isNaN(remainingMs)) return "";
  const abs = Math.abs(remainingMs);
  const totalMin = Math.round(abs / MIN_MS);
  let magnitude;
  if (totalMin < 1) magnitude = "under 1m";
  else if (totalMin < 60) magnitude = `${totalMin}m`;
  else if (totalMin < 60 * 24) magnitude = `${Math.round(totalMin / 60)}h`;
  else magnitude = `${Math.round(totalMin / (60 * 24))}d`;

  if (remainingMs <= 0) {
    return totalMin < 1 ? "due now" : `overdue ${magnitude}`;
  }
  return `${magnitude} left`;
}

/** Minutes → a friendly target label ("30m", "4h", "2d"). */
export function formatTargetMins(mins) {
  if (mins == null || Number.isNaN(mins)) return "—";
  if (mins < 60) return `${mins}m`;
  if (mins % (60 * 24) === 0) return `${mins / (60 * 24)}d`;
  if (mins % 60 === 0) return `${mins / 60}h`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
}
